import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../../components/toast/ErrorToast";
import TwoDecimalsMoneyRenderer from "../../../utils/TwoDecimalsMoneyRenderer";
import AgingTierScale from "./AgingTierScale";
import KamDailyReportPreviewFeature from "./KamDailyReportPreviewFeature";
import PaymentReminderFeature from "../payment_reminder/PaymentReminderFeature";

export default class ListCurrentAccountsFeature {

    constructor(application, navigator) {
        this._application = application;
        this._navigate = navigator;
        this._agingTierScale = AgingTierScale.default();
        this._loaded = false;
        this._currentAccounts = [];
        this._kamDailyReportPreviewFeature = new KamDailyReportPreviewFeature(application);
        this._paymentReminderFeature = new PaymentReminderFeature(application);

        makeObservable(this, {
            _loaded: observable,
            _currentAccounts: observable,
            loaded: computed,
            currentAccounts: computed,
        })
    }

    async load() {
        await this._getCurrentAccounts()
        this.finishLoading()
    }

    finishLoading() {
        this._loaded = true
    }

    get loaded() {
        return this._loaded;
    }

    get currentAccounts() {
        return this._currentAccounts;
    }

    set currentAccounts(value) {
        this._currentAccounts = value;
    }

    defaultSortModel() {
        return [{field: 'clientBusinessName', sort: 'asc'}];
    }

    selectFilters() {
        return [
            {label: 'Canal', options: this._channelOptions(), valueOf: (row) => row.channel},
            {label: 'Tramo', options: this._tierOptions(), valueOf: (row) => row.statusTierCode},
        ];
    }

    quickFilters() {
        return [
            {label: 'Con pedido futuro', predicate: (row) => row.orders},
            {label: 'Alto riesgo', predicate: (row) => row.risk},
            {label: 'Solo saldo vencido', predicate: (row) => row.overdueBalanceAmount > 0},
        ];
    }

    _channelOptions() {
        const channelsPresentInLoadedRows = this.currentAccounts.map(currentAccount => currentAccount.channel).filter(channel => channel);
        const uniqueChannels = [...new Set(channelsPresentInLoadedRows)];
        return uniqueChannels.sort((aChannel, anotherChannel) => aChannel.localeCompare(anotherChannel))
            .map(channel => ({value: channel, label: channel}));
    }

    _tierOptions() {
        return this._agingTierScale.overdueTiers().map(tier => ({value: tier.code, label: tier.label}));
    }

    columnsContent() {
        const columns = [
            {id: 'clientBusinessName', label: 'Razón Social', align: 'left'},
            {id: 'clientFantasyName', label: 'Nombre de fantasía', align: 'left'},
            {id: 'channel', label: 'Canal', align: 'left'},
            {id: 'status', label: 'Estado', sortComparator: this._statusSeverityComparator()},
            {id: 'orders', label: 'Pedidos'},
            {id: 'risk', label: 'Riesgo'},
            {id: 'outstandingBalance', label: 'Saldo Sin Vencer', align: 'right'},
            {id: 'overdueBalance', label: 'Saldo Vencido', align: 'right'},
            {id: 'unassignedBalance', label: 'Saldo Sin Asignar', align: 'right'},
            {id: 'totalBalance', label: 'Saldo Total', align: 'right'},
        ];
        if (this.hasPaymentReminderPermission()) {
            columns.push({id: 'paymentReminder', label: ''});
        }
        return columns;
    }

    hasPaymentReminderPermission() {
        return this._application.hasPaymentReminderPermission();
    }

    canSendKamDailyReports() {
        return this._application.hasSuperuserPermission();
    }

    openPaymentReminderFor(row) {
        return this._paymentReminderFeature.open(row.taxpayerIdentificationNumber);
    }

    get paymentReminderFeature() {
        return this._paymentReminderFeature;
    }

    presentedCurrentAccounts() {
        return this.currentAccounts.map(currentAccount => this._presentedCurrentAccount(currentAccount));
    }

    _presentedCurrentAccount(currentAccountResult) {
        return {
            id: currentAccountResult.customerCode,
            clientBusinessName: currentAccountResult.clientBusinessName,
            clientFantasyName: currentAccountResult.clientFantasyName,
            channel: currentAccountResult.channel || '-',
            status: currentAccountResult.worstTier ? currentAccountResult.worstTier.label : '',
            statusTierCode: currentAccountResult.worstTier ? currentAccountResult.worstTier.code : null,
            orders: currentAccountResult.hasOrderWithFutureDelivery,
            risk: currentAccountResult.hasNoOverdueTolerance,
            outstandingBalance: TwoDecimalsMoneyRenderer(currentAccountResult.outstandingBalance.amount),
            overdueBalance: TwoDecimalsMoneyRenderer(currentAccountResult.overdueBalance.amount),
            overdueBalanceAmount: currentAccountResult.overdueBalance.amount,
            unassignedBalance: TwoDecimalsMoneyRenderer(currentAccountResult.unassignedBalance.amount),
            totalBalance: TwoDecimalsMoneyRenderer(currentAccountResult.totalBalance.amount),
            taxpayerIdentificationNumber: currentAccountResult.taxpayerIdentificationNumber,
            hasOverdueInvoices: currentAccountResult.hasOverdueInvoices,
        }
    }

    _statusSeverityComparator() {
        return (aStatusLabel, anotherStatusLabel, aCellParams, anotherCellParams) =>
            this._agingTierScale.severityOf(this._tierCodeOfRowOf(aCellParams)) -
            this._agingTierScale.severityOf(this._tierCodeOfRowOf(anotherCellParams));
    }

    _tierCodeOfRowOf(cellParams) {
        return cellParams.api.getRow(cellParams.id).statusTierCode;
    }

    async _getCurrentAccounts() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this.currentAccounts = response.currentAccounts();
            },
            handlesError: (response) => {
                errorToast('No se pudieron obtener las cuentas corrientes')
            }
        });
        return this._application.apiClient().getCurrentAccounts(responseHandler)
    }

    navigateToCurrentAccountScreen(row) {
        this._navigate(`/current-accounts/${encodeURIComponent(row.clientBusinessName)}`)
    }

    get kamDailyReportPreviewFeature() {
        return this._kamDailyReportPreviewFeature;
    }

    openKamDailyReportPreview() {
        return this._kamDailyReportPreviewFeature.openPreview();
    }

}
